import React, { useCallback, useContext, useState } from 'react';
import { IEditorProps } from './Editor';
import './CharArrayEditor.less';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { useDebounceStringProperty } from 'hooks/useDebounceStringProperty';
import { IPropertyChild, getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { EditorLabel } from './EditorLabel';
import { TextField } from 'components/inputs/TextField';
import { useEditorId } from './hooks/useEditorId';
import { AwaitableContext } from 'contexts/AwaitableContext';
import { submitCharArrayValue } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';

interface ICharArrayEditorProps extends IEditorProps {}

// TODO i18n?
const getInstructions = () => 'Enter a string that will be split into a char array.';
const getCharStringFromArray = (child: IPropertyChild) => {
  return getChildValueAsArray(child).join('');
};

/**
 * Char Array editor. Splits the string value into individual characters
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function CharArrayEditor({ autoFocus, hideInputLabel, child, path, id }: ICharArrayEditorProps) {
  const { addPromise, removePromise } = useContext(AwaitableContext);
  const { editingContextId } = useContext(PropertyEditorContext);
  [id] = useEditorId(path, id);
  const [isFocused, setIsFocused] = useState(false);
  const changeHandler = useCallback(
    (value: string, path: string[]) => {
      const id = path.join(',');
      addPromise(
        id,
        (async () => {
          try {
            await submitCharArrayValue({ editingContextId, path, value });
          } catch (error) {
            console.error('Error saving char array value', error);
          } finally {
            removePromise(id);
          }
        })()
      );
    },
    [addPromise, editingContextId, removePromise]
  );

  const validation = getEditorValidationSummary(child);
  const { fieldValue, handleChange, emitOnBlur } = useDebounceStringProperty({
    value: getCharStringFromArray(child),
    path,
    onChange: changeHandler,
    skipValueUpdate: isFocused,
  });

  const onFocus = () => setIsFocused(true);
  const onBlur = () => {
    setIsFocused(false);
    emitOnBlur();
  };

  return (
    <EditorLabel
      editor={child}
      path={path}
      htmlFor={id}
      className="dpc-char-array-editor"
      hideInputLabel={hideInputLabel}
    >
      <TextField
        autoFocus={autoFocus}
        id={id}
        value={fieldValue}
        onChange={handleChange}
        validation={validation}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <span className="dpc-char-array-editor__instructions">{getInstructions()}</span>
    </EditorLabel>
  );
}
